import type { JSX } from 'react';
import { houseLayer, type HouseLayer } from '@/lib/house';
import { Icon } from './Icon';

/**
 * Which layer of the house a module belongs to.
 *
 * Deliberately quiet — an outline chip, not a filled one. The card already
 * carries a coloured icon plate and a status chip; a third loud element and the
 * eye has nowhere to rest. This is provenance, read second.
 *
 * A module may own two layers (Head to Head is a Scouting question answered
 * with Valuation arithmetic), so this renders a row, not a single chip.
 */
export function LayerChips({
  layers,
}: {
  // `| undefined` is load-bearing: the repo runs `exactOptionalPropertyTypes`,
  // under which an optional prop and a prop that may be undefined are not the
  // same type — and every caller passes a value that may be undefined.
  readonly layers?: readonly HouseLayer[] | undefined;
}): JSX.Element | null {
  if (layers === undefined || layers.length === 0) return null;
  return (
    <div className="layer-row">
      {layers.map((id) => {
        const meta = houseLayer(id);
        if (meta === undefined) return null;
        return (
          <span key={id} className="layer-chip" title={`${meta.label} \u00b7 ${meta.verb}`}>
            <Icon name={meta.icon} />
            {meta.short}
          </span>
        );
      })}
    </div>
  );
}
